// See @store/AppPhaseStore.ts for the store loading order.
// Synchronous store resolved at module load from window.location and env vars.
// Holds environment config (production vs local), backend URL, and URL query params
// that drive which grid/observation to load. No async, no dependencies on other stores.

import { create } from 'zustand';

export type AppEnv = 'production' | 'local-live' | 'local-offline' | 'demo';

type EnvStore = {
    env: AppEnv;
    isDev: boolean;
    backendUrl: string;
    gridIdFromQuery: string;
    observationId: string | null;
    projectIdFromQuery: string | null;
    reportIdFromQuery: string | null;
};

const resolveEnv = (): AppEnv => {
    if (typeof window === 'undefined') return 'local-offline';

    const query = new URL(window.location.href).searchParams;

    if (query.has('demo')) {
        return 'demo';
    }

    const domain = window.location.origin;
    if (domain.includes('urbanmetrix.com') || import.meta.env.VITE_PROD) {
        return 'production';
    }
    if (window.location.port == '3000' || import.meta.env.VITE_GO_LIVE === 'true' || query.get('api') === 'true') {
        return 'local-live';
    }
    return 'local-offline';
};

const resolveGridId = (env: AppEnv): string => {
    if (typeof window === 'undefined') return 'sampleData';

    const query = new URL(window.location.href).searchParams;

    if (env === 'demo') {
        return query.get('scene') || '';
    }

    const queryGridId = query.get('grid_id') || 'sampleData';

    if (env === 'local-live') {
        return import.meta.env.VITE_GRID_ID || queryGridId;
    }
    if (env === 'local-offline') {
        return import.meta.env.VITE_SAMPLE_GRID || queryGridId;
    }
    return queryGridId;
};

const resolveBackendUrl = (env: AppEnv): string => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';

    if (env === 'local-live') {
        return import.meta.env.VITE_BACKEND_DOMAIN || origin;
    }
    return origin;
};

const env = resolveEnv();
const query = typeof window !== 'undefined' ? new URL(window.location.href).searchParams : new URLSearchParams();

export const useEnvStore = create<EnvStore>(() => ({
    env,
    isDev: env !== 'production',
    backendUrl: resolveBackendUrl(env),
    gridIdFromQuery: resolveGridId(env),
    observationId: query.get('observation_id'),
    projectIdFromQuery: query.get('project_id'),
    reportIdFromQuery: query.get('report_id'),
}));

export const getEnvStore = () => useEnvStore.getState();

export const getReadableEnvMode = (env: AppEnv): string => {
    switch (env) {
        case 'local-live':
            return 'local live';
        case 'local-offline':
            return 'local offline';
        default:
            return env;
    }
};

export const getDisabledInEnvMessage = (env: AppEnv): string => `Disabled in ${getReadableEnvMode(env)} mode`;
